import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from './useAuth';
import { getSubscription, getPrices } from '@/lib/api/subscription';
import { getSubscriptionError } from '@/lib/errors';
import { captureFriendlyError } from '@/lib/sentry';
import { setPremiumProperty } from '@/lib/analytics';
import type { ApiError } from '@/lib/api/client';
import type { PriceCadence } from '@/types/database';
import {
  SubscriptionContext,
  type SubscriptionState,
  type SubscriptionStatus,
} from './useSubscription';

const MAX_FREE_ATTEMPTS = 5;

const INITIAL_STATE: SubscriptionState = {
  isPremium: false,
  status: 'none',
  isLoading: true,
  cadence: null,
  priceId: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  cancelAt: null,
};

function mapStatus(raw: string | null | undefined): SubscriptionStatus {
  if (!raw) return 'none';
  if (raw === 'active' || raw === 'trialing') return 'active';
  if (raw === 'canceled') return 'cancelled';
  if (raw === 'past_due' || raw === 'unpaid' || raw === 'incomplete_expired')
    return 'expired';
  return 'none';
}

export function SubscriptionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { profile, session, isAuthLoading: authLoading } = useAuth();
  const [state, setState] = useState<SubscriptionState>(INITIAL_STATE);

  const loadSubscription = useCallback(async () => {
    try {
      const [data, prices] = await Promise.all([
        getSubscription(),
        getPrices().catch(() => null),
      ]);
      const sub = data.subscription;
      let cadence: PriceCadence | null = null;
      if (sub?.price_id && prices) {
        if (prices.monthly.id === sub.price_id) cadence = 'monthly';
        else if (prices.yearly.id === sub.price_id) cadence = 'annual';
      }
      setState({
        isPremium: data.is_premium,
        status: mapStatus(sub?.status),
        isLoading: false,
        cadence,
        priceId: sub?.price_id ?? null,
        currentPeriodEnd: sub?.current_period_end ?? null,
        cancelAtPeriodEnd: sub?.cancel_at_period_end ?? false,
        cancelAt: sub?.cancel_at ?? null,
      });
      setPremiumProperty(data.is_premium);
    } catch (err) {
      const apiError = err as ApiError;
      const friendly = getSubscriptionError(
        apiError.code || 'subscription_load_failed'
      );
      captureFriendlyError(err, friendly);
      toast.error(friendly.title, { description: friendly.message });
      setState({ ...INITIAL_STATE, isLoading: false });
    }
  }, []);

  const userId = session?.user?.id ?? null;

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      setState({ ...INITIAL_STATE, isLoading: false });
      return;
    }
    loadSubscription();
  }, [authLoading, userId, loadSubscription]);

  const refreshSubscription = async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    await loadSubscription();
  };

  const checkFeatureAccess = (
    feature: 'practice' | 'interview'
  ): {
    hasAccess: boolean;
    reason?: string;
  } => {
    if (state.isPremium) {
      return { hasAccess: true };
    }

    if (feature === 'practice') {
      return {
        hasAccess: false,
        reason: 'Practice mode requires a premium subscription',
      };
    }

    if (feature === 'interview') {
      const attemptsUsed = profile?.free_attempts_used ?? 0;
      if (attemptsUsed < MAX_FREE_ATTEMPTS) {
        return { hasAccess: true };
      }
      return {
        hasAccess: false,
        reason: `You've used all ${MAX_FREE_ATTEMPTS} free mock interviews`,
      };
    }

    return { hasAccess: false };
  };

  return (
    <SubscriptionContext.Provider
      value={{
        ...state,
        refreshSubscription,
        checkFeatureAccess,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}
